import React, { useEffect } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useCampaignPerformance } from '../hooks/usePerformance';
import { PerformanceFilterInput, CampaignPerformanceItem } from '../types/performance.types';
import CampaignPerformanceTable from '../components/CampaignPerformanceTable';

interface CampaignPerformanceContainerProps {
  filter: PerformanceFilterInput;
}

const CampaignPerformanceContainer: React.FC<CampaignPerformanceContainerProps> = ({
  filter,
}) => {
  const { data, loading, error, refetch } = useCampaignPerformance();

  useEffect(() => {
    refetch(filter);
  }, [filter, refetch]);

  const exportToCSV = () => {
    if (!data || data.length === 0) return;

    // Helper function
    const rate = (total: number, fraction: number): number | string => {
      if (total === 0) return 'No Data';
      const percentage = (fraction / total) * 100;
      return Math.round(percentage);
    };

    // CSV headers
    const headers = [
      'vendorName',
      'totalLeads',
      'totalBillableLeads',
      'totalCost',
      'totalACASales',
      'totalU65Sales',
      'totalPrivateMedPremium',
      'totalAncillaries',
      'totalAncillariesPremium',
      'billRate',
    ];

    // Build CSV rows
    const rows = data.map((record: CampaignPerformanceItem) => {
      return [
        record.vendorName,
        record.totalLeads,
        record.totalBillableLeads,
        record.totalCost,
        record.totalACASales,
        record.totalU65Sales,
        record.totalPrivateMedPremium,
        record.totalAncillaries,
        record.totalAncillariesPremium,
        rate(record.totalLeads, record.totalBillableLeads),
      ];
    });

    // Convert to CSV string (comma-separated)
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `${cell}`).join(',')),
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `campaign-performance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={exportToCSV}
          disabled={loading || !data || data.length === 0}
        >
          Export CSV
        </Button>
      </div>
      <CampaignPerformanceTable
        data={data}
        loading={loading}
        error={error}
      />
    </div>
  );
};

export default CampaignPerformanceContainer;
